import { useState } from "react";
import { Link } from "react-router";

import classes from "./AddOns.module.css";
import { treatmentPages, servicesData } from "./serviceData";
import useDocumentMeta from "../../utils/useDocumentMeta";
import useSectionView from "../../utils/useSectionView";
import { trackFaqToggle, trackNavClick } from "../../utils/tracking";

import heroImage from "../../Assets/chair_closeup.jpg";

const page = treatmentPages.find((p) => p.slug === "add-ons");
const addOns = servicesData.addOns;
const findAddOn = (id) => addOns.find((a) => a.id === id);

const groups = [
  {
    label: "Pairs With: Microneedling Facial Only",
    to: "/treatments/microneedling",
    linkLabel: "View Microneedling Facial →",
    addOnIds: ["microneedling-neck-add-on", "microneedling-chest-add-on"],
  },
  {
    label: "Pairs With: Any Facial Treatment",
    to: "/treatments/advanced-facials",
    linkLabel: "View Advanced Facials →",
    addOnIds: ["dermaplaining-add-on"],
  },
];

const faqs = [
  {
    q: "Can I book an add-on on its own?",
    a: "No — add-ons are extras that extend a main treatment, so they can only be booked alongside a qualifying appointment, not by themselves.",
  },
  {
    q: "How do I add one to my booking?",
    a: "Mention it when you book online, or let us know during your consultation and we'll add the extra time and treatment to your appointment.",
  },
  {
    q: "Can I add the Microneedling Neck or Chest add-on to a different facial?",
    a: "These two are only available alongside the Microneedling Facial, since they extend that specific treatment to the neck or chest area.",
  },
];

const AddOnsPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const faqSectionRef = useSectionView("Add-Ons FAQ");

  useDocumentMeta(
    `${page.title} | Radiance Aesthetics | Hindley | Wigan`,
    page.metaDescription,
  );

  return (
    <>
      {/* Hero */}
      <section className={classes.heroSection}>
        <div className={classes.heroContainer}>
          <div className={classes.heroContent}>
            <p className={classes.heroSubtitle}>Enhance Your Treatment</p>
            <h1 className={classes.heroTitle}>Add-Ons</h1>
            <p className={classes.heroDescription}>
              Optional extras that extend selected treatments — booked alongside
              your main appointment, not on their own.
            </p>
            <Link
              to="/treatments"
              className={classes.heroCta}
              onClick={() => trackNavClick("Browse Treatments", "Add-Ons Hero")}
            >
              Browse Treatments
            </Link>
          </div>
          <div className={classes.heroImageWrapper}>
            <img
              className={classes.heroImage}
              src={heroImage}
              alt="Treatment room and equipment at Radiance Aesthetics"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Highlight banner */}
      <section className={classes.section}>
        <div className={classes.highlightBannerWrapper}>
          <div className={classes.highlightBanner}>
            <h2 className={classes.highlightTitle}>How Add-Ons Work</h2>
            <p className={classes.highlightText}>
              Add-ons are <strong>booked alongside a main treatment</strong> —
              they add extra time and coverage to your appointment, so they
              can't be booked as a standalone visit. Pick your main treatment
              first, then mention the add-on when booking or during your
              consultation.
            </p>
          </div>
        </div>
      </section>

      {/* Grouped add-ons */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>Available Add-Ons</p>
          <h2 className={classes.sectionTitle}>Extend Your Treatment</h2>
          {groups.map((group) => (
            <div className={classes.group} key={group.label}>
              <p className={classes.groupLabel}>{group.label}</p>
              <div className={classes.groupGrid}>
                {group.addOnIds.map((id) => {
                  const addOn = findAddOn(id);
                  if (!addOn) return null;
                  return (
                    <div className={classes.addonCard} key={id}>
                      <div className={classes.addonHeader}>
                        <span className={classes.addonName}>{addOn.name}</span>
                        <span className={classes.addonPrice}>
                          £{addOn.price} · {addOn.duration}
                        </span>
                      </div>
                      <p className={classes.addonText}>{addOn.description}</p>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: "16px" }}>
                <Link
                  to={group.to}
                  className={classes.addonLink}
                  onClick={() =>
                    trackNavClick(group.linkLabel, "Add-Ons Group")
                  }
                >
                  {group.linkLabel}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className={classes.section} ref={faqSectionRef}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>FAQ</p>
          <h2 className={classes.sectionTitle}>Common Questions</h2>
          <div className={classes.faqContainer}>
            {faqs.map((faq, i) => (
              <div className={classes.faqItem} key={faq.q}>
                <h3
                  className={classes.faqQuestion}
                  onClick={() => {
                    const opening = expandedFaq !== i;
                    trackFaqToggle(faq.q, i, opening);
                    setExpandedFaq(opening ? i : null);
                  }}
                  role="button"
                  aria-expanded={expandedFaq === i}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      const opening = expandedFaq !== i;
                      trackFaqToggle(faq.q, i, opening);
                      setExpandedFaq(opening ? i : null);
                    }
                  }}
                >
                  {faq.q} {expandedFaq === i ? "−" : "+"}
                </h3>
                {expandedFaq === i && (
                  <p className={classes.faqAnswer}>{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={classes.ctaSection} aria-label="Browse treatments">
        <h2 className={classes.ctaTitle}>Ready to Book?</h2>
        <p className={classes.ctaDescription}>
          Choose your main treatment first, then add on any extras you'd like
          during booking or your consultation.
        </p>
        <Link
          to="/treatments"
          className={classes.ctaButton}
          onClick={() =>
            trackNavClick("Browse Treatments", "Add-Ons Bottom CTA")
          }
        >
          Browse Treatments
        </Link>
      </section>
    </>
  );
};

export default AddOnsPage;
